"use client";

import React from "react";
import { ArrowRight, Clock, Loader, Mail, MapPin, Phone, Send } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function InquiryPortal() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="47373" id="connect-with-our-office" ref={ref} className={`relative w-full bg-background text-foreground py-16 md:py-24 overflow-hidden transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(53,128,121,0.08),transparent_38%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(17,47,75,0.05),transparent_42%)] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 xl:gap-16 items-start">
          <div className="lg:col-span-5">
            <Badge variant="secondary" className="bg-secondary/10 text-secondary hover:bg-secondary/20 border-transparent px-3 py-1.5 text-sm font-default font-bold tracking-wide mb-6">
              <Clock className="mr-2 size-3.5" />
              Response Time: Within 1 business day
            </Badge>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold tracking-tight text-primary-500 leading-tight mb-6">
              Connect with RiverBend.
            </h1>

            <p className="max-w-xl text-lg md:text-xl font-default text-muted-foreground leading-relaxed mb-8">
              Reach out to discuss financial management, reporting, advisory
              support, project work, systems, or complex organizational needs.
              We will help determine whether RiverBend is the right fit and
              what next step makes sense.
            </p>

            <div className="space-y-5">
              <div className="rounded-lg border border-border bg-card p-5 flex items-start gap-4">
                <div className="size-11 rounded-md bg-secondary/10 flex items-center justify-center shrink-0">
                  <Mail className="size-5 text-secondary" />
                </div>

                <div>
                  <p className="text-sm font-default font-bold uppercase tracking-wider text-muted-foreground mb-1">
                    Email
                  </p>
                  <a href="mailto:info@riverbendcorp.ca" className="text-xl md:text-2xl font-default font-bold text-primary-500 hover:text-secondary transition-colors break-all">
                    info@riverbendcorp.ca
                  </a>
                </div>
              </div>

              <div className="rounded-lg border border-border bg-card p-5 flex items-start gap-4">
                <div className="size-11 rounded-md bg-secondary/10 flex items-center justify-center shrink-0">
                  <Phone className="size-5 text-secondary" />
                </div>

                <div>
                  <p className="text-sm font-default font-bold uppercase tracking-wider text-muted-foreground mb-1">
                    Phone
                  </p>
                  <a href="tel:+18079357952" className="text-xl md:text-2xl font-default font-bold text-primary-500 hover:text-secondary transition-colors">
                    (807) 935-7952
                  </a>
                </div>
              </div>

              <div className="rounded-lg border border-border bg-card p-5 flex items-start gap-4">
                <div className="size-11 rounded-md bg-secondary/10 flex items-center justify-center shrink-0">
                  <MapPin className="size-5 text-secondary" />
                </div>

                <div>
                  <p className="text-sm font-default font-bold uppercase tracking-wider text-muted-foreground mb-1">
                    Office
                  </p>
                  <p className="text-primary-500 font-default font-bold leading-relaxed">
                    400-1186 Roland St.
                    <br />
                    Thunder Bay, Ontario
                    <br />
                    P7B 5M4
                  </p>
                  <p className="text-muted-foreground font-default mt-3">
                    Monday to Friday, 8:00 AM to 5:00 PM
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 rounded-2xl border border-secondary/20 bg-secondary/10 p-6">
              <h3 className="text-xl font-default font-bold text-primary-500 mb-3">
                What to include in your inquiry
              </h3>

              <p className="text-muted-foreground font-default leading-relaxed">
                A short summary of the issue, timeline, organization, and type
                of support needed is enough to start. We can follow up for more
                details if the work may be a fit.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-2xl border border-border bg-card shadow-xl p-6 sm:p-8 md:p-10">
              <div className="mb-8">
                <p className="text-secondary font-default font-bold tracking-widest uppercase text-sm mb-3">
                  Send an Inquiry
                </p>

                <h2 className="text-2xl md:text-3xl font-default font-bold text-primary-500 mb-3">
                  Tell us how we can help.
                </h2>

                <p className="text-muted-foreground font-default leading-relaxed">
                  Use this form to share the type of support you are looking
                  for. We will review your inquiry and respond with a practical
                  next step.
                </p>
              </div>

              <FormProvider formKey="contact_form_contact" formId="contact_form_contact" sectionName="inquiry-portal" className="w-full" defaultValues={{
              name: "",
              email: "",
              phone: "",
              company: "",
              project_type: "",
              budget: "",
              message: ""
            }} validationRules={{
              name: {
                required: "Name is required"
              },
              email: {
                required: "Email is required",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Please enter a valid email address"
                }
              },
              project_type: {
                required: "Please select a service interest"
              },
              message: {
                required: "Please tell us about your inquiry"
              }
            }} submitText="Send Inquiry" successMessage="Thank you for your message. A member of our team will be in touch shortly." errorMessage="Something went wrong. Please try again or call our office directly.">
                <InquiryForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function InquiryForm() {
  const {
    control,
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex min-h-[400px] flex-col items-center justify-center space-y-6 text-center rounded-2xl border border-secondary/20 bg-secondary/10 p-8 md:p-12">
        <div className="flex size-16 items-center justify-center rounded-full bg-secondary/20 text-secondary">
          <ArrowRight className="size-8" />
        </div>

        <div className="space-y-2">
          <h3 className="text-2xl font-default font-bold text-primary-500">
            Message Sent
          </h3>
          <p className="font-default text-muted-foreground max-w-md">
            {successMessage}
          </p>
        </div>

        <Button onClick={resetForm} variant="outline" className="mt-2 border-primary-500 text-primary-500 hover:bg-primary-500 hover:text-white font-default rounded-md">
          Send Another Message
        </Button>
      </div>;
  }
  const inputClasses = "h-12 rounded-md border-border bg-background font-default focus-visible:border-secondary focus-visible:ring-secondary/30";
  const selectClasses = "h-12 rounded-md border-border bg-background font-default focus-visible:border-secondary focus-visible:ring-secondary/30";
  return <Form onSubmit={handleSubmit} className="space-y-7">
      <div className="grid gap-6 md:grid-cols-2">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default">Full Name *</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default">Email Address *</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(807) 555-0000" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default">
                Organization / Band Office
              </FormLabel>
              <FormControl>
                <Input placeholder="Organization name" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <FormField control={control} name="project_type" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default">
                Service Interest *
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className={selectClasses}>
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="Advisory & Management Support">
                    Advisory & Management Support
                  </SelectItem>
                  <SelectItem value="Financial Management & Reporting">
                    Financial Management & Reporting
                  </SelectItem>
                  <SelectItem value="Year-End and Audit Readiness">
                    Year-End and Audit Readiness
                  </SelectItem>
                  <SelectItem value="Project Planning & Delivery">
                    Project Planning & Delivery
                  </SelectItem>
                  <SelectItem value="Systems, Reporting & Automation">
                    Systems, Reporting & Automation
                  </SelectItem>
                  <SelectItem value="Economic Development Support">
                    Economic Development Support
                  </SelectItem>
                  <SelectItem value="General Inquiry">
                    General Inquiry
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="budget" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default">Estimated Scope</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className={selectClasses}>
                    <SelectValue placeholder="Select a project scope" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="Initial Conversation">
                    Initial Conversation
                  </SelectItem>
                  <SelectItem value="Short-Term Support">
                    Short-Term Support
                  </SelectItem>
                  <SelectItem value="Project-Based Support">
                    Project-Based Support
                  </SelectItem>
                  <SelectItem value="Ongoing Support">
                    Ongoing Support
                  </SelectItem>
                  <SelectItem value="Not Sure Yet">
                    Not Sure Yet
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default">Inquiry Details *</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us briefly about the support you are looking for, any timelines involved, and what you would like help with." {...field} className="min-h-[140px] resize-y rounded-md border-border bg-background font-default focus-visible:border-secondary focus-visible:ring-secondary/30" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md bg-destructive/10 p-4 text-sm font-default text-destructive">
          {submitError}
        </div>}

      <div className="pt-2">
        <Button type="submit" disabled={isSubmitting} size="lg" className="h-12 px-8 bg-secondary text-white hover:bg-secondary/90 font-default rounded-md">
          {isSubmitting ? <>
              <Loader className="mr-2 size-4 animate-spin" />
              Sending...
            </> : <>
              Send Inquiry
              <Send className="ml-2 size-4" />
            </>}
        </Button>
      </div>
    </Form>;
}